import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PdfPaginas } from "./PdfPaginas";
import { useData } from "@/context/DataContext";
import { GastoForm } from "@/components/gastos/GastoForm";

const ZOOMS = [1, 1.5, 2, 3];
const nombreDe = (doc, blob) => {
  const base = (doc.sin_ia ? doc.filename : doc.original_filename) || doc.filename || doc.title || "documento";
  if (/\.[a-z0-9]{2,4}$/i.test(base)) return base;
  const ext = (blob.type.split("/")[1] || "").replace("jpeg", "jpg");
  return ext ? `${base}.${ext}` : base;
};

const BtnBar = ({ testid, onClick, icon: I, label, href, download }) => {
  const cls = "min-h-[44px] min-w-[44px] px-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm inline-flex items-center justify-center gap-1.5";
  if (href) return <a data-testid={testid} href={href} download={download} className={cls}><I size={18} /> {label}</a>;
  return <button data-testid={testid} onClick={onClick} className={cls} aria-label={label}><I size={18} />{label && <span className="hidden sm:inline">{label}</span>}</button>;
};

const CorreoTexto = ({ c }) => (
  <div className="max-w-2xl mx-auto my-3 bg-white rounded-2xl p-5 text-[#1C2421]" data-testid="visor-correo">
    {[["Asunto", c.asunto], ["Remitente", c.remitente], ["Fecha", c.fecha && c.fecha.split("-").reverse().join("/")]].filter(([, v]) => v).map(([k, v]) => (
      <p key={k} className="text-sm"><span className="font-semibold text-[#6E7A74]">{k}: </span>{v}</p>
    ))}
    {c.cuerpo && <p className="text-base whitespace-pre-wrap break-words mt-3 border-t border-[#F2EDE4] pt-3" data-testid="visor-correo-cuerpo">{c.cuerpo}</p>}
  </div>
);

// Visor a pantalla completa dentro de AL DÍA: PDF e imágenes, zoom, compartir y guardar.
export const VisorDocumento = () => {
  const [doc, setDoc] = useState(null);
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState("");
  const [zoom, setZoom] = useState(0);
  const [paginas, setPaginas] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [fallo, setFallo] = useState(false);
  const [gastoDoc, setGastoDoc] = useState(null);
  const scrollRef = useRef(null);
  const { gastos = [] } = useData();

  const cerrar = useCallback(() => {
    if (url) URL.revokeObjectURL(url);
    setDoc(null); setBlob(null); setUrl(""); setZoom(0); setPaginas(0); setPagina(1); setFallo(false);
  }, [url]);

  useEffect(() => {
    const abrir = async (e) => {
      const d = e.detail;
      setDoc(d); setBlob(null); setFallo(false); setZoom(0); setPagina(1); setPaginas(0);
      if (d.correo && !d.storage_path) return;
      try {
        const r = d.blob ? { data: d.blob } : await api.get(`/documentos/${d.id}/archivo`, { responseType: "blob" });
        setBlob(r.data); setUrl(URL.createObjectURL(r.data));
      } catch (err) {
        let msg = "No se pudo abrir el documento.";
        try { msg = formatApiErrorDetail(JSON.parse(await err.response.data.text()).detail) || msg; } catch { /* respuesta no JSON */ }
        toast.error(msg); setDoc(null);
      }
    };
    window.addEventListener("aldia-visor", abrir);
    return () => window.removeEventListener("aldia-visor", abrir);
  }, []);

  useEffect(() => {
    if (!doc) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const esc = (e) => e.key === "Escape" && cerrar();
    window.addEventListener("keydown", esc);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", esc); };
  }, [doc, cerrar]);

  const formGasto = gastoDoc && <GastoForm docId={gastoDoc} origen="documentos" onClose={() => setGastoDoc(null)} onSaved={() => setGastoDoc(null)} />;
  if (!doc) return formGasto || null;
  // Solo documentos guardados sin análisis y aún sin gasto: el gasto se completa a mano, sin IA.
  const puedeGasto = doc.sin_ia && !gastos.some((g) => g.documento_id === doc.id);
  const registrarGasto = () => { const id = doc.id; cerrar(); setGastoDoc(id); };
  const tipo = blob?.type || "";
  const esPdf = tipo === "application/pdf";
  const esImg = tipo.startsWith("image/") && !/heic|heif|tiff/.test(tipo);
  const nombre = blob ? nombreDe(doc, blob) : "";
  const archivo = blob ? new File([blob], nombre, { type: tipo }) : null;
  const puedeCompartir = !!(archivo && navigator.canShare && navigator.canShare({ files: [archivo] }));
  const compartir = async () => {
    try { await navigator.share({ files: [archivo], title: doc.title || nombre }); }
    catch (e) { if (e?.name !== "AbortError") toast.error("No se pudo compartir."); }
  };
  const z = ZOOMS[zoom];
  const visible = blob && (esPdf || esImg) && !fallo;

  return (
    <div className="fixed inset-0 z-[100] bg-[#111714] flex flex-col" data-testid="visor-documento" role="dialog" aria-modal="true" aria-label={doc.title || "Documento"}>
      <div className="flex items-center gap-2 px-3 pb-2 bg-[#1C2421]" style={{ paddingTop: "max(env(safe-area-inset-top), 8px)" }}>
        <button data-testid="visor-cerrar" onClick={cerrar} className="min-h-[44px] px-3 rounded-xl bg-white text-[#1C2421] font-bold text-sm inline-flex items-center gap-1.5"><Icons.X size={18} /> Cerrar</button>
        <p className="flex-1 min-w-0 truncate text-white text-sm font-semibold" data-testid="visor-titulo">{doc.title || nombre}</p>
        {visible && (<>
          <BtnBar testid="visor-zoom-menos" onClick={() => setZoom(Math.max(0, zoom - 1))} icon={Icons.ZoomOut} />
          <BtnBar testid="visor-zoom-mas" onClick={() => setZoom(Math.min(ZOOMS.length - 1, zoom + 1))} icon={Icons.ZoomIn} />
        </>)}
      </div>
      <div ref={scrollRef} className="flex-1 overflow-auto px-3" style={{ WebkitOverflowScrolling: "touch" }} data-testid="visor-contenido">
        {doc.correo && <CorreoTexto c={doc.correo} />}
        {!blob && !(doc.correo && !doc.storage_path) && <p className="text-white/80 text-center py-10" data-testid="visor-cargando">Abriendo el documento…</p>}
        {blob && esImg && !fallo && (
          <div className="py-3" style={{ width: z > 1 ? `${Math.min(window.innerWidth - 24, 900) * z}px` : "100%" }}>
            <img src={url} alt={doc.title || nombre} data-testid="visor-imagen" onError={() => setFallo(true)} onDoubleClick={() => setZoom(zoom ? 0 : 2)}
              className={z > 1 ? "w-full h-auto mx-auto bg-white" : "block max-w-full w-auto h-auto mx-auto bg-white object-contain"}
              style={z > 1 ? undefined : { maxHeight: "calc(100dvh - 150px)" }} />
          </div>
        )}
        {blob && esPdf && !fallo && <PdfPaginas blob={blob} zoom={z} onPaginas={setPaginas} onPagina={setPagina} onError={() => setFallo(true)} scrollRef={scrollRef} />}
        {blob && !visible && (
          <div className="text-center text-white py-12 space-y-2" data-testid="visor-no-soportado">
            <Icons.FileQuestion size={40} className="mx-auto text-white/70" />
            <p className="text-base font-semibold">Este formato no se puede mostrar aquí.</p>
            <p className="text-sm text-white/70">Ábrelo en otra aplicación o guárdalo con los botones de abajo.</p>
          </div>
        )}
      </div>
      {doc.sin_ia && (
        <div className="px-3 pt-2 bg-[#1C2421] space-y-2">
          {puedeGasto && <button data-testid="visor-registrar-gasto" onClick={registrarGasto} className="w-full min-h-[48px] rounded-xl bg-white text-[#1C2421] font-bold text-base inline-flex items-center justify-center gap-2">
            <Icons.Receipt size={18} /> Registrar como gasto
          </button>}
          <button data-testid="visor-crear-contacto" onClick={() => { window.location.href = "/contactos?nuevo=1"; }} className="w-full min-h-[48px] rounded-xl bg-white text-[#1C2421] font-bold text-base inline-flex items-center justify-center gap-2">
            <Icons.UserPlus size={18} /> Crear contacto manualmente
          </button>
        </div>
      )}
      {blob && (
        <div className="flex flex-wrap items-center justify-center gap-2 px-3 pt-2 bg-[#1C2421]" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 10px)" }}>
          {esPdf && paginas > 0 && <span className="text-white/80 text-sm mr-auto" data-testid="visor-paginas">Página {pagina} de {paginas}</span>}
          {puedeCompartir && <BtnBar testid="visor-compartir" onClick={compartir} icon={Icons.Share2} label={visible ? "Compartir / Guardar" : "Abrir en otra aplicación"} />}
          <BtnBar testid="visor-descargar" href={url} download={nombre} icon={Icons.Download} label="Descargar" />
        </div>
      )}
    </div>
  );
};
